import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { stageRig } from '../lighting/stage-light-rig.js';

// Shared-rig contract (see the framing addendum): this visualizer's declared
// relationship to the one shared StageLightRig instance. PulseCore had none
// until now - it was a flat sonar plane in an otherwise empty void, which is
// exactly what the rig fixes: the beams give the rings something to radiate
// THROUGH, and a second depth plane in front of them.
//
// The camera below (0, CENTER_Y, 7.5) is already the rig's assumed
// audience view - within a few tenths of neon-pulse.js's (0, 2.6, 7) and
// spot-wall.js's (0, 2.5, 7.2), both rig owners - so nothing is repositioned
// for it here.
const RIG = 'required';
const RIG_OWNER = 'pulsecore';
// Full strength, the library default. Both the rings and the beams are
// additive over the same frame, so this was checked rather than assumed:
// on one frozen frame the rig raises the share of the image above 0.85
// luminance from 0.80% to 0.96%, and NOTHING clips to white either way
// (peak 0.958). A reduced scale was tried first and is not needed - it only
// made PulseCore dimmer than it was before the rig existed.
const RIG_INTENSITY_SCALE = 1;
// 'floor' here means only the AIM constraint (a minimum target radius, so the
// beams sweep around the artwork instead of washing straight over it - the
// cover is the one thing in this composition that must stay readable). It does
// NOT add the lit floor plane: that one is keyed off floorLightBoost > 1, which
// this visualizer does not ask for. A floor would contradict the piece, which
// is a sonar plane in a void with no ground in it at all.
const RIG_ZONES = 'floor';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'pulsecore',
  family: 'shader',
  energyLevel: 0.55,
  bpmMin: 70,
  bpmMax: 160,
  movement: 0.35,
  danceability: 0.70,
  tonality: 0.75,
  enabled: true
};

// PulseCore, rewritten from scratch: numerous concentric rings expanding
// outward from the artwork's own center point like a radio wave/sonar
// ping, colored from the album's real palette, brightening on the beat,
// each ring's circumference swept by a traveling brightness wave that
// reads as the ring spinning in place even though it never actually
// rotates. Entirely procedural in one fragment shader on a single flat
// plane facing the camera - no discrete ring objects, no per-ring JS
// state, no spawn/recycle bookkeeping. The classic "sonar ping" shader
// technique (distance-from-center run through fract()) does the
// "continuously grow until off-screen" behavior for free: at any given
// moment the screen shows every ring whose radius currently satisfies
// d*FREQUENCY - uTime*SPEED == integer; as uTime advances those integers
// correspond to ever-larger d, so existing rings grow outward and new
// ones keep emerging from the center on their own, forever, from one
// continuous formula - not something spawned/tracked/recycled by hand.
const CENTER_Y = 2; // the CAMERA's own look-at target - screen-center, unchanged
const CAMERA_DISTANCE = 7.5;
const COVERAGE = 1.05; // plane sized just past the visible frustum, corners included
const RING_SPACING = 0.6; // world units between adjacent rings - many rings across the frame
const GROW_SPEED_BASE = 1.1; // world units/s at REFERENCE_BPM
const LINE_WIDTH = 0.07; // fraction of one ring's own spacing cycle - thin, crisp lines
const SPIN_FREQUENCY = 5; // brightness lobes traveling around each ring's circumference
const SPIN_SPEED = 1.3; // rad/s - the "spinning in place" illusion's own speed
const REFERENCE_BPM = 120;
const TEMPO_MULTIPLIER_MIN = 0.6;
const TEMPO_MULTIPLIER_MAX = 1.8;
const BEAT_PULSE_AMOUNT = 0.5;
const BEAT_PULSE_DECAY = 8;
const INTENSITY_BASE = 1.6;
const FALLBACK_COLORS = [0xff2fb0, 0x2fd8ff, 0xffb02f, 0x8f2fff, 0x2fff8f];

// party-mode.html's #overlay block (artwork + halo + title/artist) is
// centered on screen as ONE unit, but the artwork IMAGE alone sits
// above THAT block's own midpoint, since the title/artist text below it
// pulls the whole block's center down - "le centre des cercles doit
// etre vraiment au centre de l'artwork seul, pas incluant le titre".
// Measured directly from #overlay-artwork's real getBoundingClientRect()
// at 1920x1080 (46.2px above true viewport center, out of 1080px) and
// converted to world units at CAMERA_DISTANCE, rather than hand-computed
// from CSS font-metrics/line-height guesses - applied to the RING MESH
// only, not the camera's own look-at target, so the camera framing
// itself is unchanged and this is a pure "shift the content, not the
// view" offset (same reasoning as disco-squares' own
// camera-vs-content distinction elsewhere in this codebase).
const ARTWORK_CENTER_OFFSET = 0.43;

// "Plutot que le mouvement de camera, essayons des vagues comme dans
// DiscoSquares" - a rigid whole-plane tilt was tried first; this instead
// mirrors disco-squares.js's own per-tile depth wave exactly (pos.z +=
// sin(uTime*SPEED + position.x*FREQ_X + position.y*FREQ_Y) * AMOUNT),
// just applied to a densely-subdivided plane's individual vertices
// instead of individual point sprites - a traveling ripple genuinely
// undulates through the ring surface's own depth (not the whole plane
// rigidly tilting as one flat sheet), which perspective then reads as a
// wave passing through the rings themselves. Needs real subdivision
// (PLANE_SEGMENTS_*) to look smooth - a plain 1x1 plane only has 4
// vertices, nowhere near enough to displace into a visible wave.
// A continuous ring pattern doesn't sell Z-displacement as visibly as
// disco-squares' discrete tiles do (no individual object edges to make
// a size change obvious) - matching that file's own absolute amount
// read as barely-there here, so this is pushed further: bigger
// amplitude, lower frequency (broader, more legible undulation) than
// disco-squares.js's own constants.
const WAVE_AMOUNT = 1.3; // world units of Z displacement
const WAVE_FREQ_X = 0.18;
const WAVE_FREQ_Y = 0.15;
const WAVE_SPEED = 0.8;
const PLANE_SEGMENTS_X = 64;
const PLANE_SEGMENTS_Y = 36;


const vertexShader = `
  uniform float uTime;
  varying vec2 vPos;
  void main() {
    // Local plane-space XY (before this object's own position offset) -
    // already in world units, exactly the offset-from-center the ring
    // math below needs, so no separate UV remapping is needed.
    vPos = position.xy;

    // Same wave formula as disco-squares.js's own per-tile depth bob,
    // just evaluated per-vertex across this plane's own subdivision -
    // see WAVE_* above.
    vec3 pos = position;
    pos.z += sin(uTime * ${WAVE_SPEED.toFixed(4)} + position.x * ${WAVE_FREQ_X.toFixed(4)} + position.y * ${WAVE_FREQ_Y.toFixed(4)}) * ${WAVE_AMOUNT.toFixed(4)};

    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
  }
`;

const fragmentShader = `
  uniform float uTime;
  uniform float uIntensity;
  uniform float uPlaneRadius;
  uniform vec3 uColor0;
  uniform vec3 uColor1;
  uniform vec3 uColor2;
  uniform vec3 uColor3;
  uniform vec3 uColor4;
  varying vec2 vPos;

  // Avoids dynamic uniform-array indexing (not reliable on WebGL1/older
  // TV GPUs - same reasoning storm-layer.js's own colorA/colorB stepping
  // uses) - a plain branch chain over exactly 5 named color uniforms
  // instead. GLSL's mod() is the "floored" definition, so this stays in
  // [0,5) even for k < 0 (this ring's phase runs indefinitely negative
  // over a long session) with no special-casing needed.
  vec3 paletteColor(float k) {
    float m = mod(k, 5.0);
    if (m < 1.0) return uColor0;
    if (m < 2.0) return uColor1;
    if (m < 3.0) return uColor2;
    if (m < 4.0) return uColor3;
    return uColor4;
  }

  void main() {
    float d = length(vPos);
    float angle = atan(vPos.y, vPos.x);

    // Which ring cell this pixel falls in (k, an ever-increasing-then-
    // wrapping integer as d/uTime change) and how close it is to that
    // cell's own ring line.
    float phase = d / ${RING_SPACING.toFixed(4)} - uTime * ${(GROW_SPEED_BASE / RING_SPACING).toFixed(4)};
    float k = floor(phase);
    float ringPos = fract(phase);
    float ringDist = min(ringPos, 1.0 - ringPos);
    float ring = 1.0 - smoothstep(0.0, ${LINE_WIDTH.toFixed(4)}, ringDist);
    if (ring <= 0.001) discard;

    // Traveling angular brightness wave around this ring's own
    // circumference - the "spinning in place" illusion. k's own small
    // phase offset keeps neighboring rings from all spinning in lockstep,
    // so it reads as organic rather than one mechanical shared rotation.
    float spin = 0.5 + 0.5 * sin(angle * ${SPIN_FREQUENCY.toFixed(4)} + uTime * ${SPIN_SPEED.toFixed(4)} + k * 0.7);

    // Soft fade right at the center (a freshly-born ring, not a sudden
    // pop) and right at the plane's own edge (so it never reads as a
    // hard rectangular cutoff, just the ripple continuing off-frame).
    float fadeIn = smoothstep(0.0, 0.5, d);
    float fadeOut = 1.0 - smoothstep(uPlaneRadius * 0.8, uPlaneRadius, d);

    float brightness = ring * (0.35 + spin * 0.65) * fadeIn * fadeOut * uIntensity;
    vec3 color = paletteColor(k);
    gl_FragColor = vec4(color * brightness, brightness);
  }
`;

export class PulseCoreVisualizer extends Visualizer {
  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;

    camera.position.set(0, CENTER_Y, CAMERA_DISTANCE);
    camera.lookAt(0, CENTER_Y, 0);

    const vFovRad = THREE.MathUtils.degToRad(camera.fov);
    const frustumHalfHeight = CAMERA_DISTANCE * Math.tan(vFovRad / 2);
    const frustumHalfWidth = frustumHalfHeight * camera.aspect;

    // The mesh's own local Y=0 sits at world Y = CENTER_Y +
    // ARTWORK_CENTER_OFFSET, while the camera's lookAt (and so the
    // frustum's own vertical center) stays at world Y = CENTER_Y - the
    // mesh is shifted UP relative to the view, so the frustum's BOTTOM
    // edge is farther from the mesh's own local center than its top
    // edge, by exactly ARTWORK_CENTER_OFFSET. Sizing the plane only to
    // the frustum's symmetric half-height (as before) left the bottom
    // under-covered even at rest.
    const requiredHalfHeight = frustumHalfHeight + ARTWORK_CENTER_OFFSET;

    // The wave shader (WAVE_AMOUNT above) displaces vertices along local
    // Z, which - since this mesh carries no rotation - is world Z too: a
    // vertex pushed AWAY from the camera (pos.z -= WAVE_AMOUNT) sits at
    // depth CAMERA_DISTANCE + WAVE_AMOUNT instead of CAMERA_DISTANCE, so
    // its on-screen projection shrinks toward center by that same
    // ratio - "comme si la camera sortait legerement de la vague"
    // whenever the wave happens to push the plane's own edge away at
    // that moment. Sizing the plane for its WORST-CASE (fully receded)
    // apparent size, not its resting size, guarantees the visible edge
    // never recedes inside the frustum no matter the wave's phase.
    const waveMargin = (CAMERA_DISTANCE + WAVE_AMOUNT) / CAMERA_DISTANCE;

    const planeWidth = frustumHalfWidth * 2 * waveMargin * COVERAGE;
    const planeHeight = requiredHalfHeight * 2 * waveMargin * COVERAGE;
    // Half-diagonal, not half-height - otherwise the ripple would fade
    // out before ever reaching the frame's own corners.
    const planeRadius = Math.sqrt(planeWidth * planeWidth + planeHeight * planeHeight) / 2;

    const geometry = new THREE.PlaneGeometry(planeWidth, planeHeight, PLANE_SEGMENTS_X, PLANE_SEGMENTS_Y);
    this._material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uIntensity: { value: INTENSITY_BASE },
        uPlaneRadius: { value: planeRadius },
        uColor0: { value: new THREE.Color(FALLBACK_COLORS[0]) },
        uColor1: { value: new THREE.Color(FALLBACK_COLORS[1]) },
        uColor2: { value: new THREE.Color(FALLBACK_COLORS[2]) },
        uColor3: { value: new THREE.Color(FALLBACK_COLORS[3]) },
        uColor4: { value: new THREE.Color(FALLBACK_COLORS[4]) }
      },
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending
    });

    this._mesh = new THREE.Mesh(geometry, this._material);
    this._mesh.position.set(0, CENTER_Y + ARTWORK_CENTER_OFFSET, 0);
    scene.add(this._mesh);

    this._rig = stageRig.acquire(RIG_OWNER, {
      scene: scene,
      intensityScale: RIG_INTENSITY_SCALE,
      zones: RIG_ZONES
    });

    this._time = 0;
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    const bpm = profile.trackFeatures && profile.trackFeatures.bpm;
    const tempoMul = bpm
      ? THREE.MathUtils.clamp(bpm / REFERENCE_BPM, TEMPO_MULTIPLIER_MIN, TEMPO_MULTIPLIER_MAX)
      : 1;
    if (!paused) this._time += deltaTime * tempoMul;
    this._material.uniforms.uTime.value = this._time;

    const intensity = profile.director.intensity;
    const pulse = profile.director.pulse(BEAT_PULSE_AMOUNT, BEAT_PULSE_DECAY);
    this._material.uniforms.uIntensity.value = INTENSITY_BASE * (0.75 + intensity * 0.5) * pulse;

    stageRig.update(deltaTime, profile, RIG_OWNER);

    // Every ring independently takes one of the album's real vivid
    // colors (padded/cycled if fewer than 5 are available) - "les
    // cercles ont des couleurs fournies par la palette de l'artwork".
    const vivid = profile.artworkPalette && profile.artworkPalette.vividColors;
    const colors = vivid && vivid.length > 0 ? vivid : FALLBACK_COLORS;
    this._material.uniforms.uColor0.value.set(colors[0 % colors.length]);
    this._material.uniforms.uColor1.value.set(colors[1 % colors.length]);
    this._material.uniforms.uColor2.value.set(colors[2 % colors.length]);
    this._material.uniforms.uColor3.value.set(colors[3 % colors.length]);
    this._material.uniforms.uColor4.value.set(colors[4 % colors.length]);
  }

  getLightRig() {
    return this._rig;
  }

  dispose() {
    this._scene.remove(this._mesh);
    this._mesh.geometry.dispose();
    this._material.dispose();
    stageRig.release(RIG_OWNER);
  }
}
